import { getFormProps, useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import {
	type ActionFunctionArgs,
	json,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import {
	Form,
	Link,
	type MetaFunction,
	useActionData,
	useLoaderData,
} from '@remix-run/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Page, PageContent, PageTitle } from '#app/components/page.tsx'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '#app/components/ui/alert-dialog.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { requireUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { useIsPending } from '#app/utils/form.ts'
import { getIngredientString } from '#app/utils/recipe.ts'
import { invariantResponse } from '#app/utils/response.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { useOptionalUser } from '#app/utils/user.ts'

export async function loader({ params }: LoaderFunctionArgs) {
	const recipe = await prisma.recipe.findUnique({
		where: { slug: params.slug },
		select: {
			id: true,
			slug: true,
			name: true,
			instructions: true,
			ingredients: {
				select: {
					ingredient: { select: { name: true } },
					amount: true,
					unit: true,
				},
				orderBy: { index: 'asc' },
			},
			user: { select: { id: true, name: true } },
		},
	})

	invariantResponse(recipe, 'Not found', { status: 404 })

	return json({ recipe })
}

const DeleteFormSchema = z.object({
	intent: z.literal('delete-recipe'),
	recipeId: z.string(),
})

export async function action({ request }: ActionFunctionArgs) {
	const userId = await requireUserId(request)
	const data = await request.formData()

	const submission = parseWithZod(data, { schema: DeleteFormSchema })
	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { recipeId } = submission.value
	const recipe = await prisma.recipe.findUnique({
		select: { id: true, userId: true },
		where: { id: recipeId },
	})
	invariantResponse(recipe, 'Not found', { status: 404 })
	invariantResponse(recipe.userId === userId, 'Unauthorized', { status: 403 })

	await prisma.recipe.delete({ where: { id: recipe.id } })

	return redirectWithToast('/', {
		type: 'success',
		title: 'Success',
		description: 'Your recipe has been deleted.',
	})
}

export default function RecipeRoute() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()

	const canEdit = user?.id === data.recipe.user.id

	return (
		<Page>
			<PageTitle className={canEdit ? 'mb-6' : undefined}>
				<Link to="/" aria-label="Go back">
					<Icon name="arrow-left" size="lg" />
				</Link>

				<div className="relative flex-1">
					<h1 className="text-h4">{data.recipe.name}</h1>
					<p className="text-body-xs">Created by {data.recipe.user.name}</p>
					{canEdit && (
						<div className="absolute mt-1 flex gap-2">
							<Button asChild size="sm" variant="ghost">
								<Link
									to={`/recipes/${data.recipe.slug}/edit`}
									aria-label="Edit"
								>
									<Icon name="pencil" size="sm" />
								</Link>
							</Button>
							<DeleteRecipe id={data.recipe.id} />
						</div>
					)}
				</div>
			</PageTitle>

			<PageContent>
				<h2 className="text-h5">Ingredients</h2>
				<ul>
					{data.recipe.ingredients.map((ri) => (
						<li key={ri.ingredient.name}>{getIngredientString(ri)}</li>
					))}
				</ul>

				<h2 className="text-h5">Instructions</h2>
				<ol>
					{data.recipe.instructions.split('\n').map((instruction, index) => (
						<li key={instruction} className="mb-2 flex gap-2">
							<span>{index + 1}.</span>
							<span>{instruction}</span>
						</li>
					))}
				</ol>
			</PageContent>
		</Page>
	)
}

function DeleteRecipe({ id }: { id: string }) {
	const data = useActionData<typeof action>()
	const isPending = useIsPending()
	const [form] = useForm({
		id: 'delete-recipe',
		lastResult: data?.result,
	})

	return (
		<AlertDialog>
			<AlertDialogTrigger asChild>
				<Button variant="ghost" size="sm">
					<Icon name="trash" size="sm" aria-label="Edit" />
				</Button>
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>
						Are you sure you want to delete this recipe?
					</AlertDialogTitle>
					<AlertDialogDescription>
						This action cannot be undone. This will permanently delete your
						recipe.
					</AlertDialogDescription>
				</AlertDialogHeader>
				<Form method="POST" {...getFormProps(form)}>
					<AlertDialogFooter>
						<input type="hidden" name="recipeId" value={id} />
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							type="submit"
							name="intent"
							value="delete-recipe"
							disabled={isPending}
						>
							Delete
						</AlertDialogAction>
					</AlertDialogFooter>
				</Form>
			</AlertDialogContent>
		</AlertDialog>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const name = data?.recipe.name ?? 'Recipe'
	return [{ title: `Denman Dines | ${name}` }]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				403: () => (
					<div className="flex flex-col gap-6">
						<h1 className="text-h4 md:text-h2">Unauthorized</h1>
						<Link to="/" className="underline">
							<Icon name="arrow-left">Back to home</Icon>
						</Link>
					</div>
				),
				404: () => (
					<div className="flex flex-col gap-6">
						<h1 className="text-h4 md:text-h2">Recipe not found</h1>
						<Link to="/" className="underline">
							<Icon name="arrow-left">Back to home</Icon>
						</Link>
					</div>
				),
			}}
		/>
	)
}
